// Ogma's team answers "Suggest a place to look" (job L-13, Find walk-through round 2, 19 Oct 2026, "Remaining Find
// pieces"; migration 20261227130000; step 2.1). Central workspace only.
//   * read: crm_central_source_suggestions (SECURITY DEFINER), every business's suggestions. The database refuses any
//     workspace but Ogma's central one, so a client business never sees another's.
//   * write: act answer_source_suggestion (crm_central_answer_source_suggestion): "added" (optionally naming the source)
//     or "not_added" with a note. It updates that business's suggestion with an events row there and closes its
//     ogma_help task, so the business's Data sources page shows the new state and the note.
// Reads go through crm.read, writes through crm.write; never add methods to lib/crm.ts.
import type { Crm } from "./crm";
import type { Suggestion } from "./dataSources";

export interface CentralSuggestion extends Suggestion {
  tenant_id: string;
  business: string;
}

export interface CentralSuggestions {
  /** False when this workspace isn't Ogma's central one (the database refused). */
  central: boolean;
  suggestions: CentralSuggestion[];
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const STATUSES = ["sent", "looking", "added", "not_added"] as const;

/** Reads crm_central_source_suggestions tolerantly. */
export function readCentralSuggestions(raw: unknown): CentralSuggestions {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(o.suggestions) ? (o.suggestions as Record<string, unknown>[]) : [];
  return {
    central: o.central === true,
    suggestions: list
      .filter((s) => s && typeof s.id === "string" && typeof s.tenant_id === "string" && typeof s.what === "string")
      .map((s) => ({
        id: s.id as string,
        tenant_id: s.tenant_id as string,
        business: str(s.business) ?? "A business",
        what: s.what as string,
        link: str(s.link),
        why: str(s.why),
        status: (STATUSES as readonly string[]).includes(String(s.status)) ? (s.status as Suggestion["status"]) : "sent",
        reply: str(s.reply),
        created_at: str(s.created_at) ?? "",
        decided_at: str(s.decided_at),
      })),
  };
}

/** Every business's suggestions, or { central: false } when this isn't Ogma's central workspace. */
export async function centralSuggestions(crm: Crm): Promise<CentralSuggestions> {
  try {
    return readCentralSuggestions(await crm.read<unknown>("crm_central_source_suggestions", {}));
  } catch (e) {
    if (/central workspace|another business|access to that/i.test((e as Error).message ?? "")) return { central: false, suggestions: [] };
    throw e;
  }
}

export const isOpen = (s: Suggestion) => s.status === "sent" || s.status === "looking";

export type Answer = { answer: "added"; source?: string; note?: string } | { answer: "not_added"; note: string };

/** The browser's check before answering (act and the database check again). */
export function checkAnswer(a: Answer): string | null {
  const note = a.note?.trim() ?? "";
  if (a.answer === "not_added" && !note) return "Say why it isn't possible.";
  if (note.length > 700) return "Keep the note to 700 characters or fewer.";
  if (a.answer === "added" && (a.source?.trim().length ?? 0) > 200) return "Keep the source's name to 200 characters or fewer.";
  return null;
}

/** What the business will read on its Data sources page (the database writes the same words). */
export function replyPreview(a: Answer): string {
  const note = a.note?.trim() ?? "";
  if (a.answer === "not_added") return note;
  const source = a.source?.trim() ?? "";
  if (source) return `Added as ${source}.${note ? ` ${note}` : ""}`;
  return note || "Added.";
}

export function answerSuggestion(crm: Crm, s: Pick<CentralSuggestion, "id" | "tenant_id">, a: Answer): Promise<{ id: string; status: string; reply: string | null }> {
  const p: Record<string, unknown> = { business: s.tenant_id, suggestion: s.id, answer: a.answer };
  if (a.answer === "added" && a.source?.trim()) p.source = a.source.trim();
  if (a.note?.trim()) p.note = a.note.trim();
  return crm.write("answer_source_suggestion", p);
}

/** "3 waiting · oldest 4 days" */
export function waitingLine(list: CentralSuggestion[], now = Date.now()): string {
  const open = list.filter(isOpen);
  if (!open.length) return "Nothing waiting";
  const oldest = Math.min(...open.map((s) => Date.parse(s.created_at)).filter((n) => Number.isFinite(n)));
  const days = Number.isFinite(oldest) ? Math.floor((now - oldest) / 86_400_000) : 0;
  return `${open.length} waiting · oldest ${days <= 0 ? "today" : days === 1 ? "1 day" : `${days} days`}`;
}

/** "5 answered · 3 added" */
export function answeredLine(list: CentralSuggestion[]): string {
  const done = list.filter((s) => !isOpen(s));
  if (!done.length) return "None answered yet";
  const added = done.filter((s) => s.status === "added").length;
  return `${done.length} answered · ${added} added`;
}

/** The central screen's address. */
export const CENTRAL_SUGGESTIONS_PATH = "/ogma/suggestions";

/** The central workspace's slug, as the database's crm__is_central (migration 20261227130000). */
export const CENTRAL_SLUG = "ogma";

/** Whether this is Ogma's central workspace, where the team's own pages (Source suggestions) are linked. */
export function isCentralWorkspace(tenant: { slug?: string | null } | null | undefined): boolean {
  return tenant?.slug === CENTRAL_SLUG;
}
